import React, { memo, useEffect, useMemo, useState } from "react";
import InputTextComponent from "../../../components/forms/InputTextComponent";
import { Col } from "reactstrap";
import { checkFieldError } from "./CustomerFormUtil";
const rmsApiProxyRetry = require("../../../utils/api/rmsApiProxyRetry");
const commonUtility = require("../../../utils/domain/commonUtility");
const validator = require("../../../utils/validator/validator");
const translator = require("../../../utils/translator/translator");
const stringUtil = require("../../../utils/string/stringUtil");
const viewUtil = require("../../../utils/view/viewUtil");
const formatter = require("../../../utils/formatter/formatter");
const storefrontUtility = require("../../../utils/domain/storefrontUtility");

const DeliveryDistanceAndChargesBox = ({
     selectedAddress,
     deliveryDistance,
     deliveryCharges,
     blockedAddressReason,
     setBlockedAddressReason,
     setDeliveryDistance = (distance) => {},
     setDeliveryCharges = (charges) => {},
     showError,
     isDeliveryDistanceProvided,
}) => {
     const [distanceFieldLoading, setDistanceFieldLoading] = useState(false);
     const isAddressBlocked = () => {
          if (selectedAddress === null || selectedAddress === "" || selectedAddress === undefined) return false;

          const addressOverrides = commonUtility.getProperty().addressOverrides;
          let blockedAddresses = addressOverrides || [];
          blockedAddresses = blockedAddresses.filter((x) => x.isExcluded);

          const address = selectedAddress.toUpperCase();
          return blockedAddresses.some((x) => address.includes(x.address.toUpperCase()));
     };

     const getOverriddenDeliveryCost = () => {
          if (stringUtil.isStringNullOrEmpty(selectedAddress)) return null;

          const addressOverrides = commonUtility.getProperty().addressOverrides;
          let overriddenAddresses = addressOverrides || [];
          const address = selectedAddress.toUpperCase();
          return overriddenAddresses.find((x) => address.includes(x.address.toUpperCase()));
     };

     const onCalculateDistance = () => {
          setBlockedAddressReason(null);
          setDeliveryDistance("");

          if (stringUtil.isStringNullOrEmpty(selectedAddress)) return;

          const sourceAddress = commonUtility.getProperty().address;

          if (stringUtil.isStringNullOrEmpty(sourceAddress)) {
               viewUtil.showErrorAlert(translator.getLabel("delivery_charges_can_not_be_calculated_as_store_address_is_not_configured"));
               return;
          }

          const resource = `core/services/geo/distanceCalculator?source=${encodeURIComponent(sourceAddress)}&destination=${encodeURIComponent(
               selectedAddress
          )}`;
          setDistanceFieldLoading(true);
          rmsApiProxyRetry.put(resource, null, null).then(
               (result) => {
                    setDeliveryDistance(commonUtility.convertDistanceToLocalUnit(result.distance));
                    setDistanceFieldLoading(false);
               },
               (error) => {
                    viewUtil.showErrorAlert(`${translator.getLabel("error_occurred_while_calculating_the_distance")} ${error}`);
                    setDistanceFieldLoading(false);
               }
          );

          // viewUtil.showSystemModalSpinner(translator.getLabel("calculating_distance_&_charges"));
     };

     const calculateDeliveryCharge = () => {
          setBlockedAddressReason("");
          setDeliveryCharges("");

          if (!validator.isNumeric(deliveryDistance)) return;

          let distance = parseFloat(deliveryDistance);

          if (isAddressBlocked()) {
               setBlockedAddressReason(translator.getLabel("address_is_blocked"));
               return;
          }

          const overriddenDeliverCost = getOverriddenDeliveryCost();
          if (overriddenDeliverCost) {
               setDeliveryCharges(formatter.convertToAmountFormat(overriddenDeliverCost.deliveryCharges));
               return;
          }

          var maximumDistance = 5;

          const deliveryChargesConfiguration = storefrontUtility.getCurrentStorefront().deliveryChargesConfiguration || {};
          if (deliveryChargesConfiguration != null && !stringUtil.isStringNullOrEmpty(deliveryChargesConfiguration.maxDeliveryDistance))
               maximumDistance = deliveryChargesConfiguration.maxDeliveryDistance;

          if (distance > maximumDistance) {
               setBlockedAddressReason(translator.getLabel("too_far_to_deliver"));
               return;
          }

          setDeliveryCharges(calculatedDeliveryChargeFromVenueConfiguration(deliveryChargesConfiguration, distance));
     };

     const handleOnDeliveryChargesChange = (value) => {
          setDeliveryCharges(value);
     };

     const handleOnDistanceChange = (value) => {
          setDeliveryDistance(value);
     };

     const deliveryChargesValue = useMemo(() => {
          if (!stringUtil.isStringNullOrEmpty(blockedAddressReason)) return blockedAddressReason;
          return deliveryCharges;
     }, [deliveryCharges, blockedAddressReason]);

     const isDeliveryDistanceInputDisabled = useMemo(() => stringUtil.isStringNullOrEmpty(selectedAddress), [selectedAddress]);

     const isDeliveryChargesInputDisabled = useMemo(
          () => isDeliveryDistanceInputDisabled || !stringUtil.isStringNullOrEmpty(blockedAddressReason) || !validator.isNumeric(deliveryDistance),
          [isDeliveryDistanceInputDisabled, blockedAddressReason, deliveryDistance]
     );

     useEffect(() => {
          if (!isDeliveryDistanceProvided.current) {
               calculateDeliveryCharge();
          }
     }, [deliveryDistance]);

     useEffect(() => {
          if (!isDeliveryDistanceProvided.current) {
               onCalculateDistance();
          } else {
               isDeliveryDistanceProvided.current = false;
          }
     }, [selectedAddress]);

     return (
          <div className="d-flex flex-row gap-3">
               <Col>
                    <DeliveryDistanceField
                         disabled={isDeliveryDistanceInputDisabled}
                         value={deliveryDistance}
                         required
                         onChange={handleOnDistanceChange}
                         showError={showError}
                         isLoading={distanceFieldLoading}
                    />
               </Col>
               <Col>
                    <DeliveryChargesField
                         disabled={isDeliveryChargesInputDisabled}
                         value={deliveryChargesValue}
                         required
                         onChange={handleOnDeliveryChargesChange}
                         showError={showError}
                         isLoading={distanceFieldLoading}
                    />
               </Col>
          </div>
     );
};

export default memo(DeliveryDistanceAndChargesBox);

const DeliveryDistanceField = memo(({ disabled = false, value, required = false, onChange, showError, isLoading }) => {
     const [error, setError] = useState(null);

     useEffect(() => {
          if (showError) setError(checkFieldError("deliveryDistance", value));
     }, [showError, value]);

     return (
          <InputTextComponent
               autoComplete="off"
               required={required}
               type="text"
               placeholder={translator.getLabel("distance")}
               value={!isLoading ? value : translator.getLabel("loading")}
               onValueChange={onChange}
               error={error}
               disabled={disabled}
          />
     );
});

const DeliveryChargesField = memo(({ disabled = false, value, required = false, onChange, showError, isLoading }) => {
     const [error, setError] = useState(null);

     useEffect(() => {
          if (showError) setError(checkFieldError("deliveryCharges", value));
     }, [showError, value]);

     return (
          <InputTextComponent
               autoComplete="off"
               required={required}
               type="text"
               placeholder={translator.getLabel("delivery_charges")}
               value={!isLoading ? value : translator.getLabel("loading")}
               onValueChange={onChange}
               disabled={disabled}
               error={error}
          />
     );
});

function calculatedDeliveryChargeFromVenueConfiguration(storefrontDeliveryCharges, distance) {
     if (storefrontDeliveryCharges) {
          if (!stringUtil.isStringNullOrEmpty(storefrontDeliveryCharges.fixedDeliveryCharges)) {
               return Number(storefrontDeliveryCharges.fixedDeliveryCharges);
          }

          if (Array.isArray(storefrontDeliveryCharges.staggeredCharges)) {
               const deliveryCharge = storefrontDeliveryCharges.staggeredCharges.find(
                    (deliveryCharge) => distance <= Number(deliveryCharge.distance)
               );
               if (deliveryCharge) {
                    return Number(deliveryCharge.charges);
               }
          }
     }
     return distance > 3 ? 1.0 : 0.0;
}
